import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BikeDelivery } from "../Assets";
import StaticsImages from "./Statics";
import { data } from "../../utils/showcaseStatic";
import { TOAST_TAB_BASE_URL } from "../../utils/toastTabLinks";

// Hero content data for infinite scrolling
const heroContent = [
  {
    badge: "Fast Delivery",
    title: "Authentic Food",
    highlight: "At Doors!",
    description:
      "Experience the rich flavors of India with our handcrafted curries, aromatic biryanis, and tandoori specialties. Made with authentic spices and fresh ingredients.",
    image:
      "https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=1200&h=800&fit=crop",
  },
  {
    badge: "Chef's Special Biriyani",
    title: "Chef's Royal Biryani",
    highlight: "Fit for Kings",
    description:
      "Savor our signature biryani, layered with fragrant basmati rice, tender meat, and secret blend of royal spices passed down through generations.",
    image:
      "https://images.unsplash.com/photo-1563379091339-03b21ab4a4f8?w=1200&h=800&fit=crop",
  },
  {
    badge: "Chef's Tandoor Magic",
    title: "Smoky Tandoori",
    highlight: "Perfection",
    description:
      "Indulge in our clay oven specialties - perfectly charred tandoori chicken, naan bread, and kebabs that melt in your mouth.",
    image:
      "https://images.unsplash.com/photo-1567188040759-fb8a883dc6d8?w=1200&h=800&fit=crop",
  },
  {
    badge: "Traditional Feast",
    title: "Grand Thali!",
    highlight: "Experience Delievered",
    description:
      "Enjoy a complete Indian meal experience with our royal thali - a perfect combination of curries, breads, rice, and desserts on one plate.",
    image:
      "https://images.unsplash.com/photo-1601050690597-df0568f70950?w=1200&h=800&fit=crop",
  },
];

const Showcase = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % heroContent.length);
    }, 5000); // Change every 5 seconds

    return () => clearInterval(timer);
  }, []);

  const currentContent = heroContent[currentIndex];

  return (
    <section
      className="relative w-full min-h-[600px] md:min-h-[700px] overflow-hidden"
      id="home"
    >
      {/* Full-width background image with infinite scroll */}
      <div className="absolute inset-0 z-0">
        <AnimatePresence exitBeforeEnter>
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 1.2 }}
            className="absolute inset-0"
          >
            <img
              src={currentContent.image}
              alt="Hero background"
              className="w-full h-full object-cover"
            />
            {/* Dark overlay for better text readability */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Content Grid */}
      <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-2 w-full h-full px-4 md:px-8 lg:px-16 py-8 md:py-12">
        {/* Left Content - Scrolling Text */}
        <div className="flex flex-col items-start justify-center gap-4 md:gap-6">
          <AnimatePresence exitBeforeEnter>
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 50 }}
              transition={{ duration: 0.8 }}
              className="space-y-4 md:space-y-6"
            >
              <div className="flex items-center gap-2 justify-start bg-[#ac9430]/10 backdrop-blur-sm px-3 md:px-4 py-1 rounded-full w-fit">
                <p className="text-sm md:text-base text-[#ac9430] font-bold">
                  {currentContent.badge}
                </p>
                <div className="w-6 h-6 md:w-8 md:h-8 rounded-full overflow-hidden bg-white drop-shadow-xl">
                  <img
                    src={BikeDelivery}
                    alt="delivery"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>

              <h1 className="text-[2rem] md:text-[2.5rem] lg:text-[4.5rem] font-bold tracking-wide text-white leading-tight">
                {currentContent.title}
                <span className="block text-[#ac9430] text-[2.5rem] md:text-[3rem] lg:text-[5rem]">
                  {currentContent.highlight}
                </span>
              </h1>

              <p className="text-base md:text-lg text-white/90 w-full md:w-[90%] leading-relaxed backdrop-blur-sm bg-black/20 p-3 md:p-4 rounded-lg">
                {currentContent.description}
              </p>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.open(TOAST_TAB_BASE_URL, '_blank')}
                className="bg-gradient-to-br from-[#ac9430] to-[#227a32] text-white px-6 md:px-8 py-2.5 md:py-3 rounded-lg hover:shadow-xl transition-all ease-in-out duration-300 font-semibold text-base md:text-lg cursor-pointer"
              >
                Order Now
              </motion.button>
            </motion.div>
          </AnimatePresence>

          {/* Progress indicators */}
          <div className="flex gap-2 mt-2 md:mt-4">
            {heroContent.map((_, index) => (
              <motion.button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`h-1 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? "bg-[#ac9430] w-12"
                    : "bg-white/30 w-8 hover:bg-white/50"
                }`}
                whileHover={{ scale: 1.2 }}
              />
            ))}
          </div>
        </div>

        {/* Right Content - Static Menu Items */}
        <div className="relative hidden md:flex items-center justify-center">
          <StaticsImages items={data} />
        </div>
      </div>

      {/* Mobile-friendly menu items below hero */}
      <div className="md:hidden relative z-10 px-4 pb-8">
        <div className="grid grid-cols-2 gap-4">
          {data.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="cursor-pointer bg-gradient-to-br from-white via-[#fefdfb] to-[#fef9e7] backdrop-blur-md rounded-xl shadow-lg p-4 flex flex-col items-center justify-center border-2 border-[#ac9430]/20"
              whileHover={{ scale: 1.05, boxShadow: "0 15px 30px rgba(172, 148, 48, 0.3)" }}
              whileTap={{ scale: 0.95 }}
            >
              <motion.div
                whileHover={{ scale: 1.1, rotate: 360 }}
                transition={{ duration: 0.5 }}
                className="w-12 h-12 rounded-full bg-gradient-to-br from-[#ac9430] to-[#227a32] flex items-center justify-center mb-2"
              >
                <span className="text-white text-xl">🍽️</span>
              </motion.div>
              <p className="text-sm font-bold text-headingColor text-center">
                {item.title}
              </p>
              <p className="text-xs text-textColor text-center mt-1 line-clamp-2">
                {item.desc}
              </p>
              <div className="mt-2 px-3 py-1 rounded-full bg-gradient-to-r from-[#ac9430] to-[#227a32]">
                <p className="text-white font-bold text-sm">
                  <span className="text-xs">$</span> {item.price}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Showcase;
